import * as Immutable from 'immutable';
import UserStoreStore from '../_stores/Store/UserStoreStore';
import type { IUserStoreStore, UserStorePayload } from '../_stores/Store/UserStoreStore';
import { StoreStoreActionType } from '../_actions/Store/StoreStoreActions';
import type { StoreStoreAction } from '../_actions/Store/StoreStoreActions';
import { combineReducers } from 'redux-immutable';
import StoreProductReducer from './StoreProductReducer';
import { StoreProductStoreActionType } from '../_actions/Store/StoreProductStoreActions';
import type { StoreProductStoreAction } from '../_actions/Store/StoreProductStoreActions';
import ProductPublishSummaryStore from '../_stores/Store/ProductPublish/ProductPublishSummaryStore';
import type { ProductPublishSummaryPayload } from '../_stores/Store/ProductPublish/ProductPublishSummaryStore';
import ProductPublishJobStore from '../_stores/Store/ProductPublish/ProductPublishJobStore';

type StoreReducerAction = StoreStoreAction | StoreProductStoreAction;

const getProductTypes = (productTypes?: UserStorePayload['productTypes']) => {
  return Immutable.OrderedMap<string, string>().withMutations(map => {
    productTypes?.forEach(productType => {
      map.set(String(productType.id), productType.name);
    });
  });
}

const buildProductPublishSummaryStore = (
  payload: ProductPublishSummaryPayload
) => new ProductPublishSummaryStore({
  jobs: Immutable.List<ProductPublishJobStore>(
    payload.jobs.map(job => new ProductPublishJobStore(job))
  ),
});

const buildStore = (
  payload: UserStorePayload,
  oldStore?: UserStoreStore
) => {
  const {
    productTypes,
    product_publish_summary: productPublishSummaryPayload,
    ...otherProps
  } = payload;
  const productPublishSummary = productPublishSummaryPayload
    ? buildProductPublishSummaryStore(productPublishSummaryPayload)
    : oldStore?.get('product_publish_summary');

  let newStore = new UserStoreStore(otherProps);

  if (oldStore) {
    newStore = oldStore.mergeDeep(newStore);
  }

  if (productPublishSummary) {
    newStore = newStore.set('product_publish_summary', productPublishSummary);
  }

  if (productTypes) {
    newStore = newStore.set('productTypes', getProductTypes(productTypes));
  }

  return newStore;
}

const StoreReducer = (
  _store: Immutable.OrderedMap<string, UserStoreStore> = Immutable.OrderedMap<string, UserStoreStore>(),
  action: StoreReducerAction
): Immutable.OrderedMap<string, UserStoreStore> => {
  switch (action.type) {
    case StoreStoreActionType.EditStore:
      _store = _store.set(
        String(action.store.id),
        buildStore(action.store, _store.get(String(action.store.id)))
      );
      break;

    case StoreStoreActionType.EditManyStores: {
      const previousStores = _store;
      _store = Immutable.OrderedMap<string, UserStoreStore>();
      Object.values(action.stores).forEach(store => {
        _store = _store.set(
          String(store.id),
          buildStore(store, previousStores.get(String(store.id)))
        );
      })
      break;
    }

    case StoreStoreActionType.RemoveStore:
      _store = _store.remove(String(action.id));
      break;

    case StoreStoreActionType.RemoveStoreBySlug: {
      const storeKey = _store.findKey(store => store.get('slug') === action.slug);
      if (storeKey !== undefined) {
        _store = _store.remove(storeKey);
      }
      break;
    }
  }
  
  return _store.map(store => {
    const defaultStore = new UserStoreStore;

    return combineReducers<IUserStoreStore, StoreReducerAction>({
      id: _store => _store ?? defaultStore.get('id'),
      slug: _store => _store ?? defaultStore.get('slug'),
      name: _store => _store ?? defaultStore.get('name'),
      store_name: _store => _store ?? defaultStore.get('store_name'),
      store_admin_url: _store => _store ?? defaultStore.get('store_admin_url'),
      details_loaded: _store => _store ?? defaultStore.get('details_loaded'),
      autopay: _store => _store ?? defaultStore.get('autopay'),
      payment_method: _store => _store ?? defaultStore.get('payment_method'),
      billing_address: _store => _store ?? defaultStore.get('billing_address'),
      autocancel: _store => _store ?? defaultStore.get('autocancel'),
      autoship: _store => _store ?? defaultStore.get('autoship'),
      import_unsynced: _store => _store ?? defaultStore.get('import_unsynced'),
      is_syncing_products: _store => _store ?? defaultStore.get('is_syncing_products'),
      is_adding_products: _store => _store ?? defaultStore.get('is_adding_products'),
      product_publish_summary: _store => _store ?? defaultStore.get('product_publish_summary'),
      flagged: _store => _store ?? defaultStore.get('flagged'),
      error_slug: _store => _store ?? defaultStore.get('error_slug'),
      is_missing_scope: _store => _store ?? defaultStore.get('is_missing_scope'),
      productTypes: (_store, action) => {
        if (
          action.type === StoreProductStoreActionType.EditStoreProductList
          && action.storeType === store.get('slug')
          && action.data.productTypes !== undefined
        ) {
          return getProductTypes(action.data.productTypes);
        }

        return _store ?? defaultStore.get('productTypes');
      },
      products: (_store, action) => (
        action.type === StoreProductStoreActionType.EditStoreProduct
        || action.type === StoreProductStoreActionType.EditManyStoreProducts
        || action.type === StoreProductStoreActionType.EditStoreProductList
        || action.type === StoreProductStoreActionType.EditStoreProductListLoader
        || action.type === StoreProductStoreActionType.InvalidateStoreProducts
      ) && action.storeType === store.get('slug') ? StoreProductReducer(_store, action) : (_store ?? defaultStore.get('products')),
    })(store, action) as UserStoreStore
  });
}

export default StoreReducer
